/* eslint-disable no-underscore-dangle */
/**
 * The rules the Order Ledger's adjustment editing runs on, kept out of the
 * 1,800-line modal so each of them can be read — and tested — on its own.
 *
 * The ledger used to be append-only: the only write was
 * `adjustments.push(...)`. Correcting a mistake meant typing a second line
 * with a minus in front of it, so booking PU_DASH_3730272 carries a parking
 * adjustment, a stray credit, a DUPLICATE of the parking adjustment, and a
 * hand-typed reversal of one of them. Six lines to say "parking cost 800 Ft",
 * and no way to tell the correction apart from a real credit.
 *
 * The server-side twins of the identity and reversal rules live in
 * `pickuphungary-dashboard-api/src/packages/ph-core/src/Helpers/
 * OrderLineSettlement.helper.ts` — the API is what actually enforces them;
 * this is what stops the screen offering an action that would be refused.
 */

/** metadata key linking a reversal line back to the line it cancels. */
export const REVERSES_KEY = 'reverses_order_line_key';
/** metadata key holding the reason a reversal was issued. */
export const REVERSAL_REASON_KEY = 'reversal_reason';

function numberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizedDescription(value) {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Two money figures the office would read as the same number. */
function sameAmount(a, b) {
  return Math.abs(numberOrZero(a) - numberOrZero(b)) < 0.005;
}

// ───────────────────────── locks (settled lines) ─────────────────────────

/**
 * Whether a line may be edited or deleted, from the lock map the API sends
 * with the ledger.
 *
 * The map is keyed by `order_line_key` and built server-side from the only
 * two places that key is stored — `Payment.lines[]` and `Invoice.lines[]`.
 * Nothing here re-derives it from the booking: "the booking is Completed"
 * would freeze every safe line on a completed booking nobody has invoiced,
 * and leave editable a line on a Confirmed booking the partner has paid.
 */
export function locksForLine(locks, lineKey) {
  if (!locks || !lineKey) return [];
  const found = locks[lineKey];
  return Array.isArray(found) ? found : [];
}

/** One sentence naming what froze a line, for the padlock's tooltip. */
export function describeLocks(lockList) {
  if (!lockList || !lockList.length) return '';
  const names = lockList.map((l) => `${l.kind === 'invoice' ? 'invoice' : 'payment'} ${l.reference}`);
  if (names.length === 1) return `Locked by ${names[0]}`;
  return `Locked by ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ────────────────────────── duplicate detection ──────────────────────────

/**
 * Whether two adjustments are the SAME entry typed twice: same kind, same
 * three amounts, same currency, same operator attribution, same description.
 *
 * Deliberately NOT amount alone. Two 800 Ft parking lines on a booking with
 * two cars are both real; the description is what tells them apart, which is
 * exactly why it is part of the identity.
 */
export function isSameAdjustment(a, b) {
  if (!a || !b) return false;
  return (a.kind || 'adjustment') === (b.kind || 'adjustment')
    && sameAmount(a.debit, b.debit)
    && sameAmount(a.credit, b.credit)
    && sameAmount(a.credit_operator, b.credit_operator)
    && (a.currency || '') === (b.currency || '')
    && (a.metadata?.operator_id ?? null) === (b.metadata?.operator_id ?? null)
    && (a.metadata?.role ?? null) === (b.metadata?.role ?? null)
    && normalizedDescription(a.description) === normalizedDescription(b.description);
}

/**
 * The line `candidate` would duplicate, or null. `ignoreId` skips the line
 * being edited so re-saving it unchanged is not a duplicate of itself.
 */
export function findDuplicateAdjustment(lines, candidate, { ignoreId = null } = {}) {
  const skip = ignoreId ? String(ignoreId) : null;
  return (lines || []).find((line) => {
    if (skip && line?._id && String(line._id) === skip) return false;
    return isSameAdjustment(line, candidate);
  }) || null;
}

/**
 * The stored line the "Add adjustment" form would produce. Mirrors the
 * mapping `OrderLinesController.addAdjustment` applies server-side, so the
 * duplicate check on screen asks the same question the API will.
 *
 * NOTE the one thing it cannot mirror: an operator-side amount is stored in
 * the OPERATOR's settlement currency, which only the server knows. For that
 * case this stays in the entered currency, so the screen's guard is a
 * courtesy and the server's is the one that decides.
 */
export function formToAdjustmentLine(form, operatorById) {
  const customerAdj = numberOrZero(form.customer_adjustment);
  const operatorAdj = numberOrZero(form.operator_adjustment);
  const targetOpId = form.target_operator_id;
  const targetedOp = targetOpId && operatorById ? operatorById.get(targetOpId) : null;
  return {
    kind: 'adjustment',
    debit: customerAdj > 0 ? customerAdj : 0,
    credit: customerAdj < 0 ? -customerAdj : 0,
    credit_operator: operatorAdj,
    currency: form.currency,
    description: (form.description || '').trim(),
    ...(targetOpId
      ? { metadata: { operator_id: targetOpId, role: targetedOp?.role } }
      : {}),
  };
}

// ───────────────────────────── who a line is for ─────────────────────────

/**
 * The booking's operators by id — for naming a line's operator and for
 * attributing an adjustment to one.
 *
 * Two shapes feed in:
 * - Multi-operator bookings — each `order.operators[i]` is already
 *   enriched by the backend with `operator: { name, ... }`.
 * - Legacy single-operator bookings — `order.operators` is empty; the
 *   operator lives on `order.operator` (enriched the same way) keyed by
 *   `order.operator_id`. It gets the same shape as a multi-operator entry
 *   so downstream code (`op?.operator?.name`, `op?.role`) needs no branch;
 *   without it the ledger shows "Operator <id-slice>" instead of the name.
 */
export function bookingOperatorsById(order) {
  const map = new Map();
  (order?.operators || []).forEach((op) => {
    if (op?.operator_id) map.set(op.operator_id, op);
  });
  if (order?.operator_id && !map.has(order.operator_id)) {
    map.set(order.operator_id, {
      operator_id: order.operator_id,
      operator: order.operator,
      role: 'driver',
    });
  }
  return map;
}

// ──────────────────────────── reversal pairing ───────────────────────────

/**
 * Pair each reversal with the line it cancels so the table can render ONE
 * struck-through row instead of two the reader has to net out by hand.
 *
 * Returns `{ reversalByKey, reversedKeys }` — `reversalByKey` maps an
 * original's key to the line that cancels it; `reversedKeys` is the set of
 * keys that have been cancelled.
 *
 * `keyForLine` is passed in rather than imported so the caller supplies the
 * same `getOrderLineKey(line, originalIndex)` the backend used — the index
 * matters, and only the caller knows it.
 */
export function pairReversals(lines, keyForLine) {
  const reversalByKey = new Map();
  const reversed = new Set();
  (lines || []).forEach((line, i) => {
    const target = line?.metadata?.[REVERSES_KEY];
    if (typeof target === 'string' && target) {
      reversalByKey.set(target, { line, index: i, key: keyForLine(line, i) });
      reversed.add(target);
    }
  });
  return { reversalByKey, reversedKeys: reversed };
}

/** Whether this line is itself a reversal of something else. */
export function isReversalLine(line) {
  return !!line?.metadata?.[REVERSES_KEY];
}

/** The reason a reversal was issued — what the struck-through row says. */
export function reversalReason(line) {
  const reason = line?.metadata?.[REVERSAL_REASON_KEY];
  return typeof reason === 'string' && reason.trim() ? reason.trim() : '';
}

// ─────────────────────── the operator picker's scope ─────────────────────

/**
 * A Hungarian plate as it appears at the head of an operator's name.
 *
 * Two live formats on this fleet:
 *   AU-AA-670 — the current three-block form (two letters, two letters,
 *               three digits); this is what AU-AA-608 and AU-AA-670 are.
 *   ABC-123   — the older two-block form, still on older vehicles.
 *
 * Anchored at the start, because that is where the naming convention puts
 * it: `AU-AA-670 Parkolás`, `AU-AA-670 - Tóth Imre`.
 */
const PLATE_RE = /^([A-Z]{2,3}-[A-Z]{2}-\d{3}|[A-Z]{3}-\d{3})(?![A-Z0-9-])/;

/**
 * The vehicle an operator record belongs to, or null.
 *
 * Cost buckets and own-fleet drivers are both NAMED after the vehicle —
 * `AU-AA-670 Parkolás` is the parking bucket for the car whose driver sits
 * under `AU-AA-670 - Tóth Imre`. That naming IS the data model here; there is
 * no `kind` field on an Operator to ask instead. Parsing it in one tested
 * place is the least-bad version of that, and it is why the picker can offer
 * the right buckets rather than all 130 operators.
 */
export function vehicleOf(name) {
  const match = PLATE_RE.exec(String(name || '').trim().toUpperCase());
  return match ? match[1] : null;
}

/**
 * What an operator record IS, from its name:
 *   'driver' — `<plate> - <person>`, an own-fleet driver
 *   'cost'   — `<plate> <cost type>`, an expense bucket for that vehicle
 *   null     — a third-party operator, no vehicle in the name
 */
export function classifyOperator(name) {
  const plate = vehicleOf(name);
  if (!plate) return null;
  const rest = String(name).trim().slice(plate.length).trim();
  if (rest.startsWith('-')) return 'driver';
  return rest ? 'cost' : 'driver';
}

/** The cost type a bucket tracks: 'AU-AA-670 Parkolás' -> 'Parkolás'. */
export function costTypeOf(name) {
  if (classifyOperator(name) !== 'cost') return null;
  const plate = vehicleOf(name);
  return String(name).trim().slice(plate.length).trim() || null;
}

/**
 * The operators this booking's adjustment form may attribute money to,
 * grouped and labelled by what they are.
 *
 * Two groups, and the split encodes the business rule:
 *
 *   `onBooking`   — the operators actually on the booking. Always offered.
 *   `costBuckets` — per-vehicle, per-cost-type records (`AU-AA-670 Parkolás`)
 *                   for the vehicles OUR OWN drivers on this booking are
 *                   driving. An expense on an own driver is tracked as its
 *                   own cost line. A third-party operator has no buckets,
 *                   and its expense is filed against the operator itself,
 *                   which `onBooking` already offers. So a booking with no
 *                   own-fleet driver offers no buckets, which is the rule
 *                   made visible instead of remembered. (Until 2026-11-02 a
 *                   third party's expense was absorbed into its agreed
 *                   price; see Utils/expenseCostDestination.)
 *
 * Never returns the unfiltered list. Picking one of 130 by hand is how the
 * money ended up on the wrong record in the first place.
 */
export function scopeOperatorChoices({ bookingOperators = [], allOperators = [] } = {}) {
  const onBooking = bookingOperators
    .filter((op) => op && op.operator_id)
    .map((op) => ({
      id: op.operator_id,
      name: op.operator?.name || `Operator ${String(op.operator_id).slice(0, 6)}`,
      role: op.role,
      group: 'onBooking',
      vehicle: vehicleOf(op.operator?.name),
    }));

  // Vehicles our own drivers on this booking are running. A third-party
  // operator has no plate in its name and contributes none.
  const vehicles = new Set(
    onBooking
      .filter((op) => classifyOperator(op.name) === 'driver')
      .map((op) => op.vehicle)
      .filter(Boolean),
  );

  const alreadyOffered = new Set(onBooking.map((op) => String(op.id)));
  const costBuckets = vehicles.size === 0 ? [] : allOperators
    .filter((op) => {
      if (!op) return false;
      const id = op._id || op.Id || op.id;
      if (!id || alreadyOffered.has(String(id))) return false;
      return classifyOperator(op.name) === 'cost' && vehicles.has(vehicleOf(op.name));
    })
    .map((op) => ({
      id: String(op._id || op.Id || op.id),
      name: op.name,
      group: 'costBuckets',
      vehicle: vehicleOf(op.name),
      costType: costTypeOf(op.name),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { onBooking, costBuckets };
}

// ───────────────────────── the consequence preview ───────────────────────

function formatAmount(amount, currency) {
  const n = numberOrZero(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** '+800 Ft' / '-€15.00' — signed, in the line's own units. */
export function signedAmount(amount, currency) {
  const n = numberOrZero(amount);
  const sign = n < 0 ? '−' : '+';
  return `${sign}${formatAmount(Math.abs(n), currency)}`;
}

/**
 * What this form will actually do, in one sentence, BEFORE it is saved:
 * "+800 Ft will be added to AU-AA-670 Parkolás on this booking."
 *
 * A mis-attribution is free to fix while it is still on screen and expensive
 * afterwards — once it is a line, correcting it is an edit if nothing has
 * paid it and a reversal if something has.
 *
 * Returns [] when the form says nothing yet, so the caller can render
 * nothing rather than an empty box.
 */
export function describeConsequences(form, { operatorName } = {}) {
  const out = [];
  const customer = numberOrZero(form?.customer_adjustment);
  const operator = numberOrZero(form?.operator_adjustment);
  const currency = form?.currency;

  if (customer !== 0) {
    out.push(
      customer > 0
        ? `${signedAmount(customer, currency)} will be charged to the customer on this booking.`
        : `${signedAmount(customer, currency)} will be credited back to the customer on this booking.`,
    );
  }
  if (operator !== 0) {
    const who = operatorName || 'the selected operator';
    out.push(
      operator > 0
        ? `${signedAmount(operator, currency)} will be added to ${who} on this booking.`
        : `${signedAmount(operator, currency)} will be taken off what we owe ${who} on this booking.`,
    );
  }
  return out;
}

export default {
  bookingOperatorsById,
  locksForLine,
  describeLocks,
  isSameAdjustment,
  findDuplicateAdjustment,
  formToAdjustmentLine,
  pairReversals,
  isReversalLine,
  reversalReason,
  vehicleOf,
  classifyOperator,
  costTypeOf,
  scopeOperatorChoices,
  signedAmount,
  describeConsequences,
};
